import { AxiosError } from 'axios';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, Link, Typography } from '@mui/material';
import { getErrorMessageFromAuthErrorCode } from '../../api/firebase/auth/error';
import { auth } from '../../api/firebase/config';

import {
  gray100,
  primaryButtonBackgroundColor,
  gray40,
  gray20,
  white,
} from '../../constants/colors';
import { headerHeight, spHeaderHeight } from '../../constants/location';
import { AuthContext } from '../../contexts/auth';
import { authError, common } from '../../modules/error';
import { userLoginSchema } from '../../validation/user';
import type { UserLoginType } from '../../validation/user/userType';

import CustomTextfield from '../uiParts/CustomTextfield';
import Header from '../uiParts/Header';
import PasswordTextField from '../uiParts/PasswordTextfield';
import CustomSnackbar from '../uiParts/CustomSnackbar';
import CustomLoading from '../uiParts/CustomLoading';
import MetaTitle from '../utils/MetaTitle';

const styles = {
  button: {
    height: '44px',
    fontSize: '16px',
    fontWeight: '500',
  },
  pageContainer: {
    pt: headerHeight,
    backgroundColor: gray20,
    minHeight: `calc(100vh - ${headerHeight})`,
    '@media screen and (max-width: 600px)': {
      pt: spHeaderHeight,
      minHeight: `calc(100vh - ${spHeaderHeight})`,
    },
  },
  loginFormContainer: {
    alignItems: 'center',
    backgroundColor: white,
    borderRadius: '4px',
    border: `1px solid ${gray40}`,
    flexDirection: 'column',
    p: '40px',
    width: '400px',
    '@media screen and (max-width: 600px)': {
      width: '100%',
      padding: '32px 16px 34px 16px',
      boxSizing: 'border-box',
      margin: '0 16px',
    },
  },
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: `calc(100vh - ${headerHeight})`,
    '@media screen and (max-width:600px)': {
      height: `calc(100vh - ${spHeaderHeight})`,
      width: '100%',
    },
    width: '560px',
    margin: '0 auto',
  },
  link: {
    color: gray100,
    display: 'block',
    fontWeight: '500',
    m: '24px 0px 0px 0px',
  },
  title: {
    '@media screen and (max-width:600px)': {
      fontSize: '20px',
    },
    color: gray100,
    fontSize: '22px',
    fontWeight: '500',
    mb: '24px',
    textAlign: 'left',
  },
};

const AuthLogin: React.FC = (): JSX.Element => {
  const [errorText, setErrorText] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [firstSubmit, setFirstSubmit] = useState(false);
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<UserLoginType>({
    resolver: yupResolver(userLoginSchema),
  });

  const buttonDisabledFlag = (): boolean => {
    if (firstSubmit) {
      return (
        watch('email') === undefined ||
        '' ||
        watch('password') === undefined ||
        '' ||
        Boolean(errors.email) ||
        Boolean(errors.password)
      );
    }
    return false;
  };

  // ログイン処理
  const onSubmit = async (): Promise<void> => {
    try {
      setLoading(true);
      const userAuth = await signInWithEmailAndPassword(auth, watch('email'), watch('password'));
      if (!userAuth) throw new Error(authError.signInFailed);
    } catch (error: unknown) {
      let errorText: string;
      if (error instanceof FirebaseError) {
        errorText = getErrorMessageFromAuthErrorCode(error.code);
      } else if (error instanceof AxiosError && error.response) {
        errorText = error.response.data.message;
      } else {
        errorText = common;
      }
      setErrorText(errorText);
      setOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // ログイン状態の場合はAI-OCR画面へ遷移
    if (user) navigate('/ai-ocr');
  }, [user]);

  return (
    <Typography component="div" sx={styles.pageContainer}>
      <MetaTitle />
      <CustomLoading loading={loading} />
      <Header />
      <Typography component="div" sx={styles.container}>
        <Typography component="div" sx={styles.loginFormContainer}>
          <Typography sx={styles.title}>ログイン</Typography>
          <Controller
            render={({ field }): JSX.Element => (
              <CustomTextfield
                {...field}
                error={Boolean(errors.email)}
                formMb="24px"
                helperText={errors.email?.message}
                labelText="メールアドレス"
                placeholder="xxx@cto-hands.co.jp"
                requiredText="必須"
                fullWidth
              />
            )}
            control={control}
            name="email"
          />

          <Controller
            render={({ field }): JSX.Element => (
              <PasswordTextField
                {...field}
                error={Boolean(errors.password)}
                formMb="40px"
                helperText={errors.password?.message}
                labelText="パスワード"
                requiredText="必須"
              />
            )}
            control={control}
            name="password"
          />

          <Button
            disabled={buttonDisabledFlag()}
            fullWidth
            onClick={async (): Promise<void> => {
              setFirstSubmit(true);
              await handleSubmit(async () => onSubmit())();
            }}
            sx={[primaryButtonBackgroundColor, styles.button]}
            variant="contained"
          >
            ログイン
          </Button>

          <Link
            color="inherit"
            href="/auth/password_reset"
            sx={[styles.link, { width: 'fit-content' }]}
          >
            パスワードをお忘れの方はこちら
          </Link>
        </Typography>
      </Typography>
      <CustomSnackbar
        autoHideDuration={6000}
        open={open}
        onClose={(): void => setOpen(false)}
        text={errorText}
      />
    </Typography>
  );
};

export default AuthLogin;
